import React from "react";

import { Navigate, Route, Routes } from "react-router-dom";

import Redirect from "Components/Redirect";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentSelector } from "Reducers/environment";
import {
  selectFeatureHttpMetricsEnabled,
  selectFeatureProfilingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import RedirectToFirstApp from "src/organization/common/containers/ServicesDisplay/ServicesList/RedirectToFirstApp";
import { useAppSelector } from "Store/hooks";

import PluginApp from "../app/Page";
import ProjectPage from "../settings/Page";

import ActionsRoutes from "./actions/Routes";
import BackupsRoute from "./backups/Routes";
import DetailPage from "./detail/Page";
import useDisableHttpMetricsForNonUsageBilled from "./metrics/hooks/useDisableHttpMetricsForNonUsageBilled";
import { getDeploymentType } from "./metrics/hooks/useMetricsLinkData";
import HttpMetrics from "./metrics/http/Page";
import HttpMetrics2 from "./metrics/http2/Page";
import ProfilingPage from "./metrics/profiling/Page";
import MetricsPage from "./metrics/resources/Page";
import MetricsPage2 from "./metrics/resources/Page2";
import useObservabilitySandboxAccess from "./observabilitySandbox/hooks/useObservabilitySandboxAccess";
import ObservabilitySandboxPage from "./observabilitySandbox/Page";
import Page from "./Page";
import EnvironmentServicesRoutes from "./services/EnvironmentServicesRoutes";
import SettingsRoutes from "./settings/Routes";

const EnvironmentRoutes = () => {
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();
  const profilingEnabled = useAppSelector(selectFeatureProfilingEnabled);
  const environment = useSelectorWithUrlParams(environmentSelector);
  const enableHttpMetrics = useAppSelector(selectFeatureHttpMetricsEnabled);
  const canAccessObservabilitySandbox = useObservabilitySandboxAccess();
  const hasMetrics = !environment || environment?.getLinks()?.["#metrics"];
  const disableHttpMetricsForNonUsageBilled =
    useDisableHttpMetricsForNonUsageBilled();
  const metricsType = useAppSelector(getDeploymentType);

  const allowHttpMetricsRoute =
    enableHttpMetrics && hasMetrics && !disableHttpMetricsForNonUsageBilled;

  return (
    <Routes>
      <Route
        path="settings/*"
        element={
          <ProjectPage>
            <SettingsRoutes />
          </ProjectPage>
        }
      />
      <Route
        path="metrics"
        element={
          <Page>
            <MetricsPage />
          </Page>
        }
      />
      <Route
        path="metrics2"
        element={
          <Page>
            <MetricsPage2 />
          </Page>
        }
      />
      <Route
        path="http-traffic"
        element={
          !metricsType ? null : allowHttpMetricsRoute ? (
            <HttpMetrics />
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route
        path="http-traffic2"
        element={
          enableHttpMetrics && hasMetrics ? (
            <HttpMetrics2 />
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route path="profiling" element={profilingEnabled && <ProfilingPage />} />

      <Route
        path="observability-sandbox"
        element={canAccessObservabilitySandbox && <ObservabilitySandboxPage />}
      />

      <Route
        path="activities"
        element={
          <Redirect
            to={`/${organizationId}/${projectId}/${encodeURIComponent(
              environmentId
            )}/backups/history`}
          />
        }
      />
      <Route path="backups/*" element={<BackupsRoute />} />

      <Route
        path="actions/*"
        element={
          <DetailPage>
            <ActionsRoutes />
          </DetailPage>
        }
      />
      <Route path="services" element={<RedirectToFirstApp />} />
      <Route
        path="services/:appName/*"
        element={<EnvironmentServicesRoutes />}
      />
      <Route
        path="-/app/:appId"
        element={
          <PluginApp
            route="/:organizationId/:projectId/:environmentId/"
            type="tab"
            organizationId={organizationId}
            projectId={projectId}
            environmentId={environmentId}
          />
        }
      />
      <Route path="log/:activityId" element={<DetailPage />} />
      <Route path="" element={<DetailPage />} />
    </Routes>
  );
};

export default EnvironmentRoutes;
